import { useState } from 'react';

import { Select } from '@/components/common/select';
import { TextButton } from '@/components/common/text-button';
import { Drawer } from '@/components/feedback/drawer';
import { useCursorSelect } from '@/hooks/use-cursor-select';

import { listDealerCities } from '../api';
import { DEALER_STATUS_LABEL } from '../constants';
import type { DealerCityOption, DealerListFilters, DealerStatus } from '../types';

import styles from './dealer-filter-drawer.module.scss';

/** The label map's key order is the AC's (Activation Pending, Active, Inactive). */
const STATUS_OPTIONS = (Object.keys(DEALER_STATUS_LABEL) as DealerStatus[]).map((value) => ({
  value,
  label: DEALER_STATUS_LABEL[value],
}));

const EMPTY: Required<Omit<DealerListFilters, 'search'>> = { city: [], status: [] };

/** Varies by search term, which is what resets `useCursorSelect` to the first page. Inline until
 *  the feature grows a key factory (05). */
const cityKey = (search: string) => ['dealers', 'meta', 'cities', search] as const;

/** The option value IS the "City, ST" label — what the listing's `city` filter matches — so a
 *  selected city stays labelled after a search narrows the options away from it. */
const cityToOption = (c: DealerCityOption) => ({ value: c.label, label: c.label });

type DealerFilterDrawerProps = {
  open: boolean;
  /** Last applied filters; the draft re-seeds from this on each open. */
  value: DealerListFilters;
  onApply: (filters: DealerListFilters) => void;
  onClose: () => void;
};

type BodyProps = Omit<DealerFilterDrawerProps, 'open'>;

function FilterBody({ value, onApply, onClose }: BodyProps) {
  const applied = { city: value.city ?? [], status: value.status ?? [] };
  const [draft, setDraft] = useState(applied);

  const citySelect = useCursorSelect({
    queryKey: cityKey,
    fetchPage: async (params) => {
      const page = await listDealerCities(params);
      return { data: page.data, ...page.meta };
    },
    toOption: cityToOption,
  });

  const empty = draft.city.length === 0 && draft.status.length === 0;

  return (
    <Drawer
      open
      onClose={onClose}
      title="Filter"
      primaryText="Apply"
      primaryDisabled={empty}
      onPrimary={() => {
        onApply(draft);
        onClose();
      }}
      cancelText="Cancel"
      onCancel={onClose}
      footerLeft={
        <div className={styles.footerLeft}>
          <TextButton size="sm" onClick={() => setDraft(EMPTY)}>
            Clear Filter
          </TextButton>
          <TextButton size="sm" onClick={() => setDraft(applied)}>
            Reset
          </TextButton>
        </div>
      }
    >
      {/* Status above City, as Figma 1913:836387 draws them. The two do not cascade. */}
      <div className={styles.fields}>
        <div className={styles.field}>
          <span className={styles.label}>Status</span>
          <Select
            aria-label="Status"
            mode="multiple"
            placeholder="Select"
            showSearch
            style={{ width: '100%' }}
            options={STATUS_OPTIONS}
            emptyText="No statuses found."
            value={draft.status}
            onChange={(next) => setDraft((d) => ({ ...d, status: next as DealerStatus[] }))}
          />
        </div>
        <div className={styles.field}>
          <span className={styles.label}>City</span>
          <Select
            aria-label="City"
            mode="multiple"
            placeholder="Select"
            style={{ width: '100%' }}
            showSearch={citySelect.showSearch}
            loading={citySelect.loading}
            onPopupScroll={citySelect.onPopupScroll}
            options={citySelect.options}
            // The designed empty panel (Figma 216:264119), not antd's "No data"; the Assign
            // Installer picker's wording for a server-searched list that is still loading.
            emptyText={citySelect.loading ? 'Searching…' : 'No cities found.'}
            onOpenChange={(isOpen) => {
              if (!isOpen) citySelect.resetSearch();
            }}
            value={draft.city}
            onChange={(next) => setDraft((d) => ({ ...d, city: next as string[] }))}
          />
        </div>
      </div>
    </Drawer>
  );
}

export function DealerFilterDrawer({ open, ...rest }: DealerFilterDrawerProps) {
  if (!open) {
    return (
      <Drawer open={false} onClose={rest.onClose}>
        {null}
      </Drawer>
    );
  }
  // Unmount on close (Cancel, ✕, mask alike); remounts fresh so the draft re-seeds from `value`.
  return <FilterBody {...rest} />;
}
